import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import { Icon, type IconName } from '@/components/Icon'
import {
  Carousel,
  useCarouselNavigationArrowAlignmentRef,
} from '@/components/Carousel'
import { useIsExtraSpacious, useIsSpacious } from '@/hooks/viewport-hooks'
import { useLightDarkFn } from '@/features/shell/theming/color-scheme-hooks'
import type { ServiceTypeInfoCard } from './service-type-info-content'

import styles from './ServiceTypeInfoDialog.module.css'

interface ServiceTypeInfoDialogProps {
  isOpen: boolean
  displayTitle: string
  icon: IconName
  middleCard: ServiceTypeInfoCard
  firstCard: ServiceTypeInfoCard
  lastCard: ServiceTypeInfoCard
  onClose: () => unknown
  onStartNow: () => unknown
  testId?: string
}

export function ServiceTypeInfoDialog({
  isOpen,
  displayTitle,
  icon,
  middleCard,
  firstCard,
  lastCard,
  onClose,
  onStartNow,
  testId,
}: ServiceTypeInfoDialogProps) {
  const isSpacious = useIsSpacious()
  const isExtraSpacious = useIsExtraSpacious()
  const lightDark = useLightDarkFn()
  const cards = [firstCard, middleCard, lastCard]

  function resolveImageSrc(card: ServiceTypeInfoCard) {
    const { images } = card
    return lightDark(
      isSpacious ? images.light : images.lightMobile,
      isSpacious ? images.dark : images.darkMobile,
    )
  }

  return (
    <ModalDialog size="wide" isOpen={isOpen} onClose={onClose} testId={testId}>
      <ModalDialogHeader>
        <ModalDialogTitle>
          <span className={styles.titleWithIcon}>
            Before you start, here&apos;s how{' '}
            <span className={styles.titleServiceType}>
              {displayTitle}{' '}
              <Icon name={icon} variant="surface-active" mini inline /> works
            </span>
          </span>
        </ModalDialogTitle>
        <ModalDialogDescription>
          Get a quick overview of what to expect.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogContent>
        {isExtraSpacious ? (
          <div className={styles.cardGrid}>
            {cards.map((card, i) => (
              <InfoCard key={i} card={card} imageSrc={resolveImageSrc(card)} />
            ))}
          </div>
        ) : (
          <Carousel testId={testId ? `${testId}-carousel` : undefined}>
            {cards.map((card, i) => (
              <InfoCard key={i} card={card} imageSrc={resolveImageSrc(card)} />
            ))}
          </Carousel>
        )}
      </ModalDialogContent>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Start now"
            variant="default"
            onPress={onStartNow}
            testId={testId ? `${testId}-start-now-button` : undefined}
          />
          <Button
            label="Back"
            variant="outline"
            onPress={onClose}
            testId={testId ? `${testId}-back-button` : undefined}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface InfoCardProps {
  card: ServiceTypeInfoCard
  imageSrc: string
}

function InfoCard({ card, imageSrc }: InfoCardProps) {
  const navigationArrowAlignmentRef = useCarouselNavigationArrowAlignmentRef()
  return (
    <div className={styles.card}>
      <div className={styles.cardImage} ref={navigationArrowAlignmentRef}>
        <div className={styles.cardImageFrame}>
          <img
            src={imageSrc}
            alt=""
            aria-hidden="true"
            draggable={false}
            className={styles.cardIllustration}
          />
        </div>
      </div>
      <div className={styles.cardText}>
        <div className={styles.cardTitle}>{card.title}</div>
        <div className={styles.cardDescription}>{card.description}</div>
      </div>
    </div>
  )
}
